import { useContext } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { DonationBadge } from "@features/JointBillList/components/JointBillPeriod/components/JointBill/components/JointBillTable/components/DonationBill/components/DonationBadge/DonationBadge";
import { DonationBillTotalCell } from "@features/JointBillList/components/JointBillPeriod/components/JointBill/components/JointBillTable/components/DonationBill/components/DonationBillTotalCell/DonationBillTotalCell";
import { ProviderBillNameCell } from "@features/JointBillList/components/JointBillPeriod/components/JointBill/components/JointBillTable/components/ProviderBill/components/ProviderBillNameCell/ProviderBillNameCell";
import { JointBillTableRowLayout } from "@features/JointBillList/components/JointBillPeriod/components/JointBill/layouts/JointBillTableRowLayout/JointBillTableRowLayout";
import { JointBillContext } from "@features/JointBillList/utils";
import { Badge } from "@components/Badge/Badge";
import { DotIcon } from "@components/icons";
import { Link } from "@components/Link/Link";
import {
  useSelectDonationBillMutation,
  useUnselectDonationBillMutation,
} from "@services/bills/mutations";
import {
  type IDonationData,
  type IOverviewJointBillService,
} from "@services/overview/types";

interface IDonationBillProps {
  jointBillServiceId: IOverviewJointBillService["jointBillServiceId"];
  periodId: number;
  donationData: IDonationData;
}

export const DonationBill: React.FC<IDonationBillProps> = ({
  jointBillServiceId,
  periodId,
  donationData,
}) => {
  const { t } = useTranslation();
  const { isSummaryMode } = useContext(JointBillContext);
  const {
    description,
    canBeSelected,
    isSelected,
    totalAmount = 0,
    donationServiceProviderId,
    additionalInformationUrl,
    isPaid,
  } = donationData;

  const { mutateAsync: selectDonationBill, isPending: isSelectLoading } =
    useSelectDonationBillMutation({
      jointBillServiceId,
      periodId,
      donationServiceProviderId,
    });

  const { mutateAsync: unselectDonationBill, isPending: isUnselectLoading } =
    useUnselectDonationBillMutation({
      jointBillServiceId,
      periodId,
      donationServiceProviderId,
    });

  const isLoading = isSelectLoading || isUnselectLoading;

  const toggleSelection = () => {
    if (isSelected) {
      unselectDonationBill().catch(() => {
        toast.error(t("common.unknownErrorOccurred"));
      });
    } else {
      selectDonationBill(totalAmount).catch(() => {
        toast.error(t("common.unknownErrorOccurred"));
      });
    }
  };

  return (
    <JointBillTableRowLayout
      nameCell={
        <ProviderBillNameCell
          name={
            <div className="flex flex-col gap-1 md:flex-row">
              <p
                className="truncate text-base-black md:text-grey-dark"
                title={description}
              >
                {description}
              </p>
              <Link
                to={additionalInformationUrl}
                target="_blank"
                rel="nofollow"
              >
                {t("bill.donation.action")}
              </Link>
            </div>
          }
          providerBadge={<DonationBadge />}
        />
      }
      statusCell={
        isPaid && (
          <Badge
            color="green"
            variant="bubble"
            icon={DotIcon}
            iconClassName="size-2"
            size="small"
          >
            {t("bill.billState.paid")}
          </Badge>
        )
      }
      {...(!isSummaryMode || isSelected
        ? {
            totalCell: (
              <DonationBillTotalCell
                currency="EUR"
                amount={totalAmount}
                isCheckboxDisabled={!canBeSelected}
                isCheckboxChecked={isSelected}
                isCheckboxLoading={isLoading}
                onCheckboxClick={toggleSelection}
              />
            ),
          }
        : {})}
    />
  );
};
